import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CmSheetDevelopmentCommentsService from "../../services/api/cmSheet/CmSheetComments.service";
import { UpdateCollaboratorCmSheetDevelopmentCommentsRequest } from "../../types/api/collaborators.type";
import { useNotification } from "../notification.hook";
import { useEffect } from "react";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

export const CM_SHEET_DEVELOPMENT_COMMENTS_QUERY_KEY =
  "get-cm-sheet-development-comments";

export const useCmSheetDevelopmentComments = (cmSheetId: number) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_DEVELOPMENT_COMMENTS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetDevelopmentCommentsService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: !!cmSheetId,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);
  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetDevelopmentComments = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateComments, isPending } = useMutation({
    mutationFn: (body: UpdateCollaboratorCmSheetDevelopmentCommentsRequest) =>
      CmSheetDevelopmentCommentsService.update(cmSheetId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CM_SHEET_DEVELOPMENT_COMMENTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY, cmSheetId],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });
  return { updateComments, isPending };
};
